import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { BackgroundCheckPanel } from '../../components/BackgroundCheckPanel';
import { Icon } from '../../components/Icon';
import { RedFlagsBadges } from '../../components/RedFlagsBadges';
import { type Customer, customerApi } from '../../lib/api';

export const Route = createFileRoute('/customers/$customerId')({
  component: CustomerDetailPage,
});

const STAGE_LABELS: Record<Customer['stage'], string> = {
  prospect: '潜在',
  contacted: '已联系',
  quoted: '已报价',
  sampled: '已样品',
  ordered: '已大货',
  delivered: '已交付',
  lost: '流失',
};

const COUNTRY_FLAGS: Record<string, string> = {
  JP: '🇯🇵',
  US: '🇺🇸',
  GB: '🇬🇧',
  CN: '🇨🇳',
  DE: '🇩🇪',
  FR: '🇫🇷',
  KR: '🇰🇷',
};

function CustomerDetailPage() {
  const { customerId } = useParams({ from: '/customers/$customerId' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: customer,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => customerApi.get(customerId),
  });

  const [editing, setEditing] = useState(false);
  const [editStage, setEditStage] = useState<Customer['stage']>('prospect');
  const [editNotes, setEditNotes] = useState('');

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<Customer>) => customerApi.update(customerId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setEditing(false);
    },
  });

  function startEditing() {
    if (!customer) return;
    setEditStage(customer.stage);
    setEditNotes(customer.notes ?? '');
    setEditing(true);
  }

  if (isLoading) {
    return <div className="text-ink-3 text-sm">加载中...</div>;
  }
  if (error || !customer) {
    return (
      <div>
        <Link to="/customers" className="text-sm text-accent inline-flex items-center gap-1">
          <Icon name="arrow-left" size="xs" strokeWidth={2} />
          返回客户列表
        </Link>
        <div className="mt-4 p-4 bg-bad/10 border border-bad rounded text-bad">
          客户不存在或无权限访问
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/customers" className="text-sm text-accent inline-flex items-center gap-1 mb-3">
        <Icon name="arrow-left" size="xs" strokeWidth={2} />
        返回客户列表
      </Link>

      <div className="bg-surface border border-line rounded p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl inline-flex items-center">
                <Icon name="building" size="lg" className="text-ink-3" />
              </span>
              <h1 className="text-xl font-semibold">{customer.name}</h1>
              {customer.tags.map((t) => (
                <span key={t} className="tag text-xs">
                  {t}
                </span>
              ))}
              {/* v1.13: 来自官网表单的客户徽章 (source=web3forms) */}
              {customer.source === 'web3forms' && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 border border-blue-200"
                  title="首次来源: toei-sosan.com 官网联系表单 (Web3Forms webhook)"
                >
                  🌐 来自官网询盘
                </span>
              )}
            </div>
            <div className="text-sm text-ink-2">
              {customer.city && `${customer.city} · `}
              {customer.country}
              {customer.website && (
                <>
                  {' · '}
                  <a
                    href={customer.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent hover:underline"
                  >
                    {customer.website}
                  </a>
                </>
              )}
            </div>
          </div>
          {!editing ? (
            <button
              type="button"
              onClick={startEditing}
              className="px-3 py-1.5 border border-line rounded text-sm hover:bg-surface-2"
            >
              编辑
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="px-3 py-1.5 border border-line rounded text-sm hover:bg-surface-2"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => updateMutation.mutate({ stage: editStage, notes: editNotes })}
                disabled={updateMutation.isPending}
                className="px-3 py-1.5 bg-accent text-white rounded text-sm hover:bg-accent-2 disabled:opacity-50"
              >
                保存
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-4 text-sm">
          <div>
            <div className="text-xs text-ink-3 mb-1">阶段</div>
            {editing ? (
              <select
                value={editStage}
                onChange={(e) => setEditStage(e.target.value as Customer['stage'])}
                className="px-2 py-1 border border-line rounded"
              >
                {Object.entries(STAGE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            ) : (
              <div className="font-medium">{STAGE_LABELS[customer.stage]}</div>
            )}
          </div>
          <div>
            <div className="text-xs text-ink-3 mb-1">来源</div>
            <div className="font-medium">{customer.source}</div>
          </div>
          <div>
            <div className="text-xs text-ink-3 mb-1">签约偏好</div>
            <div className="font-medium">
              {customer.incotermPref ?? '—'}
              {customer.paymentPref ? ` · ${customer.paymentPref}` : ''}
            </div>
          </div>
        </div>

        {/* v1.9.1: 红旗信号显示在 main info 区 */}
        {(customer.redFlags ?? []).length > 0 && (
          <div className="mt-4 pt-3 border-t border-line">
            <div className="text-xs text-ink-3 mb-1.5">红旗信号</div>
            <RedFlagsBadges customerId={customerId} redFlags={customer.redFlags ?? []} />
          </div>
        )}

        {customer.firstOrderPayment && (
          <div className="mt-3 flex items-center gap-2 text-xs">
            <span className="text-ink-3">推荐首单付款:</span>
            <code className="px-2 py-0.5 bg-surface-2 rounded font-mono">
              {customer.firstOrderPayment}
            </code>
          </div>
        )}
      </div>

      <div className="bg-surface border border-line rounded p-5 mb-4">
        <h2 className="text-sm font-semibold mb-2">备注</h2>
        {editing ? (
          <textarea
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border border-line rounded text-sm"
            placeholder="客户背景 / 决策人 / 跟进记录..."
          />
        ) : (
          <p className="text-sm text-ink-2 whitespace-pre-wrap">
            {customer.notes || <span className="text-ink-3">（无）</span>}
          </p>
        )}
      </div>

      {/* v1.9.2: 背调 / 风险评估 panel (含一键扫描) */}
      <div className="mb-4">
        <BackgroundCheckPanel customerId={customerId} />
      </div>

      <div className="bg-surface border border-line rounded p-5 mb-4">
        <h2 className="text-sm font-semibold mb-2">时间线</h2>
        <p className="text-xs text-ink-3">
          时间线聚合（询盘 / 报价 / 订单 / 单证）将在 v1 W3 实现。当前显示客户基础信息。
        </p>
      </div>

      <div className="bg-surface border border-line rounded p-5">
        <h2 className="text-sm font-semibold mb-3">快捷操作</h2>
        <div className="flex gap-2 text-sm">
          <button
            type="button"
            className="px-3 py-1.5 border border-line rounded hover:bg-surface-2 inline-flex items-center gap-1"
            onClick={() => navigate({ to: '/orders' })}
          >
            <Icon name="file-text" size="xs" strokeWidth={2} />
            创建报价
          </button>
          <button
            type="button"
            className="px-3 py-1.5 border border-line rounded hover:bg-surface-2 inline-flex items-center gap-1"
            onClick={() => navigate({ to: '/coach' })}
          >
            <Icon name="sparkles" size="xs" strokeWidth={2} />
            问教练
          </button>
        </div>
      </div>
    </div>
  );
}
